import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { User, Plus, FolderOpen, Link as LinkIcon, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Creator Hub - Context Selector
 * Purpose: Select which creator context to work in
 * Does NOT manage settings (those belong to Account)
 */
export default function CreatorHub() {
  const navigate = useNavigate();
  const [creators, setCreators] = useState([]);
  const [activeCreatorId, setActiveCreatorId] = useState(null);
  const [showNewCreatorModal, setShowNewCreatorModal] = useState(false);
  const [newCreatorName, setNewCreatorName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCreators();
  }, []);

  const loadCreators = () => {
    // Load from localStorage (safe default since no backend API specified)
    try {
      const stored = localStorage.getItem('consistency_creators');
      const storedActive = localStorage.getItem('consistency_active_creator');
      
      if (stored) {
        const parsed = JSON.parse(stored);
        setCreators(parsed);
      } else {
        // Default creator if none exists
        const defaultCreator = {
          id: 'default',
          name: 'My Creator',
          symbol: null,
          dropboxConnected: false,
          platformCount: 0
        };
        setCreators([defaultCreator]);
        localStorage.setItem('consistency_creators', JSON.stringify([defaultCreator]));
      }
      
      if (storedActive) {
        setActiveCreatorId(storedActive);
      }
    } catch (error) {
      console.error('Error loading creators:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCreator = () => {
    if (!newCreatorName.trim()) {
      toast.error('Creator name is required');
      return;
    }

    const newCreator = {
      id: `creator_${Date.now()}`,
      name: newCreatorName.trim(),
      symbol: null,
      dropboxConnected: false,
      platformCount: 0
    };

    const updated = [...creators, newCreator];
    setCreators(updated);
    localStorage.setItem('consistency_creators', JSON.stringify(updated));
    
    // Set as active
    setActiveCreatorId(newCreator.id);
    localStorage.setItem('consistency_active_creator', newCreator.id);
    
    setShowNewCreatorModal(false);
    setNewCreatorName('');
    toast.success('Creator created');
    
    // Redirect to Create
    navigate('/app/create/content-studio');
  };

  const handleOpenCreator = (creatorId) => {
    setActiveCreatorId(creatorId);
    localStorage.setItem('consistency_active_creator', creatorId);
    toast.success('Creator context set');
    navigate('/app/create/content-studio');
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Creator Hub</h1>
          <p className="text-brand-text-muted">Select creator context</p>
        </div>
        <Button onClick={() => setShowNewCreatorModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Creator
        </Button>
      </div>

      {/* Creator Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {creators.map((creator) => (
          <Card key={creator.id} className="glass hover:bg-white/5 transition-all">
            <CardHeader>
              <div className="flex items-center gap-3 mb-3">
                {/* Creator Symbol/Avatar */}
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-blue to-brand-teal flex items-center justify-center text-white font-semibold">
                  {creator.symbol ? (
                    <img src={creator.symbol} alt={creator.name} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    getInitials(creator.name)
                  )}
                </div>
                <div className="flex-1">
                  <CardTitle className="text-base">{creator.name}</CardTitle>
                  {activeCreatorId === creator.id && (
                    <Badge className="bg-brand-blue/20 text-brand-blue border-brand-blue/30 text-xs mt-1">
                      Active
                    </Badge>
                  )}
                </div>
              </div>

              {/* Read-only Badges */}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="text-xs">
                  <FolderOpen className="h-3 w-3 mr-1" />
                  {creator.dropboxConnected ? 'Dropbox Connected' : 'No Dropbox'}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  <LinkIcon className="h-3 w-3 mr-1" />
                  {creator.platformCount} Platform{creator.platformCount !== 1 ? 's' : ''}
                </Badge>
              </div>
            </CardHeader>

            <CardContent>
              <div className="flex flex-col gap-2">
                {/* Primary CTA */}
                <Button
                  onClick={() => handleOpenCreator(creator.id)}
                  className="w-full"
                  variant={activeCreatorId === creator.id ? 'outline' : 'default'}
                >
                  Open Creator
                </Button>

                {/* Secondary CTA - Deep-link to Account */}
                <Button
                  variant="ghost"
                  className="w-full text-sm"
                  onClick={() => navigate('/app/account/settings')}
                >
                  <ExternalLink className="h-3 w-3 mr-2" />
                  Edit Creator Profile
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* New Creator Modal */}
      <Dialog open={showNewCreatorModal} onOpenChange={setShowNewCreatorModal}>
        <DialogContent className="bg-brand-bg border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Create New Creator</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white mb-2">Creator Name *</Label>
              <Input
                value={newCreatorName}
                onChange={(e) => setNewCreatorName(e.target.value)}
                placeholder="Enter creator name"
                className="bg-brand-surface border-white/10 text-white"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateCreator()}
              />
            </div>

            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
              <p className="text-sm text-blue-400">
                Symbol upload and platform connections can be configured in Account → Creator Profile after creation.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCreatorModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateCreator}>
              <Plus className="h-4 w-4 mr-2" />
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
